import React from 'react'

/**
 * Single-tile skeleton that mirrors the rendered `tile-v2` DOM 1:1 so the
 * hand-off from skeleton → real tile is reflow-free:
 *
 *   • 16:9 hero region (matches `.tile-v2__hero-link--default` padding-bottom 56.25%)
 *   • Optional series-label corner badge (matches `.tile-v2__label.tile-v2__label--series`,
 *     positioned `top: -8px; left: -8px`).  The label is absolutely positioned
 *     so showing it on some skeletons and not others is layout-neutral — it's
 *     visual texture, not space reservation.
 *   • Bottom-metadata block with a title line and a tag/sub-line (matches
 *     `.tile-v2__bottom-metadata-metadata` → `.tile-v2__bottom-metadata-title`
 *     and `.tile-v2__bottom-metadata-bottom` heights).
 *
 * Classes are community-scoped (`.community-tile-skeleton__*`) — see
 * `_CommunityProfileCarousels.scss`.  We deliberately do NOT add new modifiers
 * to the shared `_Tile.v2.scss` (community-scope rule).
 *
 * `aria-hidden` keeps screen readers silent until the real tiles announce
 * themselves; N empty cells should not be narrated.
 */
interface TileV2SkeletonProps {
  /** Render a corner-badge placeholder mirroring `.tile-v2__label--series`.
   *  Useful for rows that mix series + non-series tiles so the skeleton
   *  visually previews both variants. Layout-neutral either way. */
  showSeriesLabel?: boolean
}

function TileV2Skeleton ({
  showSeriesLabel = false,
}: TileV2SkeletonProps): JSX.Element {
  return (
    <div className="community-tile-skeleton" aria-hidden>
      <div className="community-tile-skeleton__hero-wrapper">
        {showSeriesLabel ? (
          <div className="community-tile-skeleton__label" />
        ) : null}
        <div className="community-tile-skeleton__hero" />
      </div>
      <div className="community-tile-skeleton__metadata">
        <div className="community-tile-skeleton__title-line" />
        <div className="community-tile-skeleton__sub-line" />
      </div>
    </div>
  )
}

export default React.memo(TileV2Skeleton)
